// ABOUTME: Per-tab storage of the active market: sessionStorage for the tab, localStorage for the last used.
// ABOUTME: Read by the fetch wrapper and the Apollo link, written on switch and by the `/api/user` sync.

import { LAST_MARKET_KEY, SESSION_KEY } from './constants';
import type { MarketContext } from './types';

type StorageKind = 'sessionStorage' | 'localStorage';

// Storage can be unavailable (privacy modes, sandboxed frames): every access is guarded, and a
// missing storage simply means "no market stored".
const getStorage = (kind: StorageKind): Storage | undefined => {
    try {
        return (globalThis as unknown as Record<StorageKind, Storage | undefined>)[kind];
    } catch {
        return undefined;
    }
};

const readKey = (kind: StorageKind, key: string): string | null => {
    try {
        return getStorage(kind)?.getItem(key) ?? null;
    } catch {
        return null;
    }
};

const writeKey = (kind: StorageKind, key: string, value: string) => {
    try {
        getStorage(kind)?.setItem(key, value);
    } catch {
        // storage full or unavailable: the tab falls back to the server's default market
    }
};

const removeKey = (kind: StorageKind, key: string) => {
    try {
        getStorage(kind)?.removeItem(key);
    } catch {
        // storage unavailable: nothing was stored
    }
};

/** The tab's active market id: the tab's own choice, else the last market used in any tab. */
export const getActiveMarketId = (): string | null =>
    readKey('sessionStorage', SESSION_KEY) ?? readKey('localStorage', LAST_MARKET_KEY);

/** Stores the market chosen in this tab, and remembers it as the last used one. */
export const setActiveMarket = (marketId: string) => {
    writeKey('sessionStorage', SESSION_KEY, marketId);
    writeKey('localStorage', LAST_MARKET_KEY, marketId);
};

/**
 * Stores the market the server validated for this tab. With the module off (no context) or no
 * market available, any market stored earlier is cleared, so the fetch wrapper stays a
 * pass-through and no stale `x-active-market` keeps being sent.
 */
export const syncActiveMarketFromServer = (marketContext: MarketContext | null | undefined) => {
    const activeMarketId = marketContext?.activeMarketId;
    if (!activeMarketId) {
        removeKey('sessionStorage', SESSION_KEY);
        removeKey('localStorage', LAST_MARKET_KEY);
        return;
    }
    writeKey('sessionStorage', SESSION_KEY, activeMarketId);
    if (readKey('localStorage', LAST_MARKET_KEY) === null) {
        writeKey('localStorage', LAST_MARKET_KEY, activeMarketId);
    }
};
